import React, { useEffect, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
    LayoutDashboard,
    Brain,
    FolderTree,
    Building,
    Library,
    Database,
    BarChart,
    Settings,
    FileText,
    Users,
    User,
    Activity,
    CreditCard,
    ChevronDown,
    ChevronRight,
    ChevronLeft,
    Globe,
    Code,
    LucideProps
} from 'lucide-react';
import { Nav, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../../scss/sidebar.scss';

interface SidebarProps {
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    userRole: string;
}

interface NavItem {
    to: string;
    icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>>;
    label: string;
    roles: string[];
    subItems?: SubNavItem[];
}

interface SubNavItem {
    to: string;
    label: string;
    icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>>;
    roles: string[];
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggleCollapse, userRole }) => {
    const [expandedItems, setExpandedItems] = React.useState<Record<string, boolean>>(() => {
        const savedState = localStorage.getItem('sidebarState');
        return savedState ? JSON.parse(savedState) : {};
    });

    const logRole = useCallback(() => {
        if (process.env.NODE_ENV !== 'production') {
            const storageKey = 'lastRoleLog';
            const now = Date.now();
            const lastLog = localStorage.getItem(storageKey);
            const lastLogTime = lastLog ? parseInt(lastLog) : 0;

            if (now - lastLogTime > 5000) {
                console.log('Current user role:', userRole);
                localStorage.setItem(storageKey, now.toString());
            }
        }
    }, [userRole]);

    useEffect(() => {
        logRole();
    }, [logRole]);

    useEffect(() => {
        if (isCollapsed) {
            setExpandedItems({});
        }
    }, [isCollapsed]);

    useEffect(() => {
        localStorage.setItem('sidebarState', JSON.stringify(expandedItems));
    }, [expandedItems]);

    useEffect(() => {
        localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
    }, [isCollapsed]);

    const renderIcon = (Icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>>, addMargin: boolean = true) => {
        return <Icon
            size={18}
            className={`${addMargin ? 'me-2' : ''}`.trim()}
        />;
    };

    const navItems: NavItem[] = useMemo(() => [
        {
            to: "/organizations",
            icon: Building,
            label: "Organizations",
            roles: ['system_admin']
        },
        {
            to: "/dashboard",
            icon: LayoutDashboard,
            label: "Dashboard",
            roles: ['admin']
        },
        {
            to: "/sources",
            icon: Database,
            label: "Data Sources",
            roles: ['admin'],
            subItems: [
                {
                    to: "/data-sources",
                    icon: FileText,
                    label: "Files",
                    roles: ['admin']
                },
                {
                    to: "/data-sources/web",
                    icon: Globe,
                    label: "Websites",
                    roles: ['admin']
                },
                {
                    to: "/data-sources/api",
                    icon: Code,
                    label: "APIs",
                    roles: ['admin']
                }
            ]
        },
        {
            to: "/knowledge-bases",
            icon: Library,
            label: "Knowledge Bases",
            roles: ['admin']
        },
        {
            to: "/rag-applications",
            icon: Brain,
            label: "RAG Applications",
            roles: ['user', 'admin']
        },
        {
            to: "/directory",
            icon: FolderTree,
            label: "Directory",
            roles: ['admin'],
            subItems: [
                {
                    to: "/directory/users",
                    icon: User,
                    label: "Users",
                    roles: ['admin']
                },
                {
                    to: "/directory/groups",
                    icon: Users,
                    label: "Groups",
                    roles: ['admin']
                }
            ]
        },
        {
            to: "/analytics",
            icon: BarChart,
            label: "Analytics",
            roles: ['admin'],
            subItems: [
                {
                    to: "/analytics/usage",
                    icon: Activity,
                    label: "Usage",
                    roles: ['admin']
                }
            ]
        },
        {
            to: "/organization/settings",
            icon: Settings,
            label: "Settings",
            roles: ['system_admin', 'admin'],
            subItems: [
                {
                    to: "/organization/settings/billing",
                    icon: CreditCard,
                    label: "Subscription",
                    roles: ['admin']
                },
                {
                    to: "/organization/settings/billing/payment",
                    icon: CreditCard,
                    label: "Payment",
                    roles: ['admin']
                }
            ]
        }
    ], []);

    const toggleExpand = useCallback((itemTo: string, event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isCollapsed) {
            setExpandedItems(prev => ({
                ...prev,
                [itemTo]: !prev[itemTo]
            }));
        }
    }, [isCollapsed]);

    const filteredNavItems = useMemo(() => navItems.filter(item => {
        const hasMainPermission = item.roles.includes(userRole);

        if (item.subItems) {
            const filteredSubItems = item.subItems.filter(subItem =>
                subItem.roles.includes(userRole)
            );

            if (hasMainPermission || filteredSubItems.length > 0) {
                return { ...item, subItems: filteredSubItems };
            }
            return false;
        }

        return hasMainPermission;
    }), [navItems, userRole]);

    return (
        <div className={`sidebar-container ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-content">
                <div className="collapse-button-container">
                    <Button
                        onClick={onToggleCollapse}
                        className="collapse-button"
                        variant="link"
                        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        {isCollapsed ?
                            renderIcon(ChevronRight) :
                            renderIcon(ChevronLeft)
                        }
                    </Button>
                </div>

                <Nav className="flex-column gap-1 p-2" variant="pills">
                    {filteredNavItems.map((item) => (
                        <div key={item.to} className="w-100">
                            {item.subItems ? (
                                <>
                                    <Button
                                        onClick={(e) => toggleExpand(item.to, e)}
                                        className="nav-link d-flex align-items-center w-100"
                                        variant="link"
                                        title={isCollapsed ? item.label : undefined}
                                    >
                                        <div className="d-flex align-items-center">
                                            {renderIcon(item.icon, !isCollapsed)}
                                            {!isCollapsed && <span>{item.label}</span>}
                                        </div>
                                        {!isCollapsed && (
                                            expandedItems[item.to] ?
                                                renderIcon(ChevronDown) :
                                                renderIcon(ChevronRight)
                                        )}
                                    </Button>

                                    <Nav
                                        className={`sub-items-container ${expandedItems[item.to] && !isCollapsed ? 'expanded' : 'collapsed'}`}
                                    >
                                        {item.subItems
                                            .filter(subItem => subItem.roles.includes(userRole))
                                            .map(subItem => (
                                                <Nav.Item key={subItem.to}>
                                                    <NavLink
                                                        to={subItem.to}
                                                        end
                                                        title={isCollapsed ? subItem.label : undefined}
                                                        className={({ isActive }) => `
                                                            nav-link d-flex align-items-center mt-1
                                                            ${isActive ? 'active' : ''}
                                                            ${isCollapsed ? 'collapsed-item' : ''}
                                                        `}
                                                    >
                                                        {renderIcon(subItem.icon, !isCollapsed)}
                                                        {!isCollapsed && <span className="font-medium">{subItem.label}</span>}
                                                    </NavLink>
                                                </Nav.Item>
                                            ))}
                                    </Nav>
                                </>
                            ) : (
                                <Nav.Item>
                                    <NavLink
                                        to={item.to}
                                        title={isCollapsed ? item.label : undefined}
                                        className={({ isActive }) => `
                                            nav-link d-flex align-items-center
                                            ${isActive ? 'active' : ''}
                                            transition-all
                                            hover:bg-gray-100
                                        `}
                                    >
                                        {renderIcon(item.icon, !isCollapsed)}
                                        {!isCollapsed && <span className="font-medium">{item.label}</span>}
                                    </NavLink>
                                </Nav.Item>
                            )}
                        </div>
                    ))}
                </Nav>
            </div>

            <div className="sidebar-footer">
                {!isCollapsed && (
                    <div className="text-muted small">
                        © {new Date().getFullYear()} OrangeFennec
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(Sidebar);